import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { usePanelScopeRegistry } from './usePanelScopeRegistry';
import { useSelectionChannel } from './useSelectionChannel';

import type { SelectionMode } from './useSelectionChannel';

/**
 * Phase 2: PanelNavigation Store (v2: PanelManager)
 *
 * Panel 열기/닫기 상태를 관리하는 전역 store입니다.
 * - openBottomPanel: 하단 패널 열기 (List 화면용)
 * - openSidePanel: 우측 패널 열기 (Detail 화면용, dedup + embedded 차단)
 * - closePanel: 패널 닫기
 * - resetScope: Scope 초기화 (Provider 언마운트 시)
 * - syncWithURL: Phase 2에서는 no-op (향후 Phase 7 확장 지점)
 *
 * @note Phase 6에서 리네이밍:
 * - openListPanel → openBottomPanel (패널 위치 기반)
 * - openDetailPanel → openSidePanel (패널 위치 기반)
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Panel Type: 패널 유형
 * - 'list': 목록 패널
 * - 'detail': 상세 패널
 */
export type PanelType = 'list' | 'detail';

/**
 * Panel View Mode: 패널 표시 모드
 * - 'standalone': 페이지에서 패널 생성 허용
 * - 'embedded': 패널 내부에서 패널 생성 금지
 *
 * @note Phase 2 기준, 향후 확장 가능
 */
export type PanelViewMode = 'standalone' | 'embedded';

/**
 * Panel ID: 패널 식별자
 * - string: 단일 선택 (Phase 2)
 * - string[]: 다중 선택 (Phase 4)
 *
 * @phase4 Phase 4에서 배열 지원 추가
 */
export type PanelId = string | string[];

/**
 * 패널 기본 정보
 */
export interface PanelInfo {
  /**
   * 패널 리소스 타입
   * @example 'zone', 'record', 'user'
   */
  panelResource: string;

  /**
   * 패널 유형
   */
  type: PanelType;

  /**
   * 패널 표시 모드
   */
  viewMode: PanelViewMode;

  /**
   * 패널 ID (detail 타입일 때만 존재)
   * - string: 단일 선택
   * - string[]: 다중 선택 (Phase 4)
   * @example 'zone-123' | ['zone-1', 'zone-2']
   */
  panelId?: PanelId;
}

/**
 * 패널 상태 구조
 *
 * PanelNavigation에 저장되는 scope별 패널 상태입니다.
 */
export interface PanelState {
  /**
   * 패널 정보
   */
  panel: PanelInfo;

  /**
   * 선택 정보 (읽기용 캐시)
   *
   * @note 원본은 SelectionChannel에 있으며,
   * PanelNavigation은 가드/리셋 편의를 위해 복사본을 보관합니다.
   */
  selection?: {
    ids: string[];
    mode: SelectionMode;
  };
}

/**
 * openBottomPanel 파라미터 (List 화면용 하단 패널)
 *
 * @note Phase 6에서 OpenListPanelParams에서 리네이밍됨
 */
export interface OpenBottomPanelParams {
  /**
   * 패널 ID (렌더링에 필요)
   * - string: 단일 선택
   * - string[]: 다중 선택
   */
  panelId: PanelId;

  /**
   * 패널 리소스 타입 (Registry에서 조회)
   */
  panelResource: string;

  /**
   * Scope 키
   */
  scopeKey: string;

  /**
   * 패널 표시 모드 (Provider가 결정)
   */
  viewMode: PanelViewMode;
}

/**
 * openSidePanel 파라미터 (Detail 화면용 우측 패널)
 *
 * @note Phase 6에서 OpenDetailPanelParams에서 리네이밍됨
 */
export interface OpenSidePanelParams {
  /**
   * 패널 ID
   * - string: 단일 선택
   * - string[]: 다중 선택 (Phase 4)
   */
  panelId: PanelId;

  /**
   * 패널 리소스 타입 (Registry에서 조회)
   */
  panelResource: string;

  /**
   * Scope 키
   */
  scopeKey: string;

  /**
   * 패널 표시 모드 (Provider가 결정)
   */
  viewMode: PanelViewMode;
}

// ============================================================================
// Legacy Type Aliases (Backward Compatibility)
// ============================================================================

/**
 * @deprecated Use OpenBottomPanelParams instead
 */
export type OpenListPanelParams = OpenBottomPanelParams;

/**
 * @deprecated Use OpenSidePanelParams instead
 */
export type OpenDetailPanelParams = OpenSidePanelParams;

// ============================================================================
// Store Interface
// ============================================================================

interface PanelNavigationState {
  /**
   * Scope별 패널 상태 Map
   */
  panels: Map<string, PanelState>;
}

interface PanelNavigationActions {
  /**
   * 패널 닫기
   *
   * @param scopeKey - Scope 키
   */
  closePanel(scopeKey: string): void;

  /**
   * 하단 패널 열기 (List 화면용)
   *
   * @param params - 패널 파라미터
   *
   * @note Phase 6에서 openListPanel에서 리네이밍됨
   */
  openBottomPanel(params: OpenBottomPanelParams): void;

  /**
   * @deprecated Use openSidePanel instead
   */
  openDetailPanel(params: OpenSidePanelParams): void;

  /**
   * @deprecated Use openBottomPanel instead
   */
  openListPanel(params: OpenBottomPanelParams): void;

  /**
   * 우측 패널 열기 (Detail 화면용)
   *
   * 동일한 패널 요청은 no-op 처리됩니다 (dedup).
   * viewMode='embedded'일 때는 패널 생성이 차단됩니다.
   *
   * @param params - 패널 파라미터
   *
   * @note Phase 6에서 openDetailPanel에서 리네이밍됨
   */
  openSidePanel(params: OpenSidePanelParams): void;

  /**
   * Scope 초기화
   *
   * Provider 언마운트 시 호출됩니다.
   *
   * @param scopeKey - Scope 키
   */
  resetScope(scopeKey: string): void;

  /**
   * URL 동기화
   *
   * @note Phase 2에서는 no-op,
   * 향후 필요 시 PanelNavigation 내부에서만 구현
   *
   * @phase7-extension URL serialization 연동 지점
   */
  syncWithURL(): void;
}

export type PanelNavigationStore = PanelNavigationState & PanelNavigationActions;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * PanelId가 다중 선택(배열)인지 확인합니다.
 *
 * @param panelId - 확인할 패널 ID
 * @returns 배열이면 true, 단일 문자열이면 false
 *
 * @phase4 Phase 4에서 다중 선택 지원
 *
 * @example
 * ```ts
 * if (isMultiPanelId(panelId)) {
 *   // panelId는 string[]
 *   console.log('Multiple zones:', panelId.length);
 * } else {
 *   // panelId는 string
 *   console.log('Single zone:', panelId);
 * }
 * ```
 */
export function isMultiPanelId(panelId: PanelId): panelId is string[] {
  return Array.isArray(panelId);
}

// ============================================================================
// Constants
// ============================================================================

const STORE_NAME = 'PANEL_NAVIGATION';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * 패널 중복 여부 확인 (dedup용)
 *
 * Phase 4 확장:
 * - 단일 ID: 문자열 직접 비교
 * - 다중 ID: 배열 정렬 후 비교
 * - 단일/배열 혼합: 타입이 다르므로 dedup 제외
 *
 * @example
 * ```ts
 * isSamePanel(existing, { panelId: 'zone-1', ... }); // 단일 비교
 * isSamePanel(existing, { panelId: ['zone-1', 'zone-2'], ... }); // 배열 정렬 비교
 * isSamePanel(existing, { panelId: ['zone-2', 'zone-1'], ... }); // 정렬 후 동일
 * ```
 */
function isSamePanel(existing: PanelState | undefined, params: OpenSidePanelParams): boolean {
  if (!existing) return false;

  const { panel } = existing;

  // 기본 조건: type, resource, viewMode가 동일해야 함
  if (
    panel.type !== 'detail' ||
    panel.panelResource !== params.panelResource ||
    panel.viewMode !== params.viewMode
  ) {
    return false;
  }

  const existingId = panel.panelId;
  const newId = params.panelId;

  // panelId가 없으면 다른 패널로 간주
  if (!existingId || !newId) return false;

  // Phase 4: 단일/배열 타입 분기
  const existingIsArray = isMultiPanelId(existingId);
  const newIsArray = isMultiPanelId(newId);

  // 타입이 다르면 dedup 제외 (단일 vs 배열)
  if (existingIsArray !== newIsArray) {
    return false;
  }

  // 둘 다 배열: 정렬 후 비교
  if (existingIsArray && newIsArray) {
    if (existingId.length !== newId.length) return false;

    const sortedExisting = [...existingId].sort();
    const sortedNew = [...newId].sort();

    return sortedExisting.every((id, index) => id === sortedNew[index]);
  }

  // 둘 다 단일: 직접 비교
  return existingId === newId;
}

// ============================================================================
// Store Implementation
// ============================================================================

/**
 * Phase 2: PanelNavigation Store (v2: PanelManager)
 *
 * Panel 열기/닫기 상태를 전역으로 관리합니다.
 * - Dedup: 동일한 Detail 패널 재호출 시 no-op
 * - Embedded 차단: embedded mode에서는 패널 생성 금지
 * - Bridge Mode 체크: bridge-off 모드 로그 (Phase 7 연동 지점)
 *
 * @note Phase 6에서 함수명 리네이밍:
 * - openListPanel → openBottomPanel
 * - openDetailPanel → openSidePanel
 */
export const usePanelNavigation = create<PanelNavigationStore>()(
  devtools(
    (set, get) => ({
      panels: new Map<string, PanelState>(),

      // ========================================================================
      // openBottomPanel (List 화면용 하단 패널)
      // ========================================================================

      openBottomPanel: params => {
        const { scopeKey, panelResource, viewMode, panelId } = params;

        // Embedded 모드 차단
        if (viewMode === 'embedded') {
          console.warn(
            `[PanelNavigation] Blocked: embedded mode cannot create panel (${scopeKey})`,
          );
          return;
        }

        // Bridge Mode 체크
        const config = usePanelScopeRegistry.getState().getScopeConfig(scopeKey);
        if (config?.bridgeMode === 'off') {
          console.info(`[PanelNavigation] bridge-off mode: ${scopeKey}`);
          // @phase7-extension: 레거시 연동 차단 지점
          // Phase 2에서는 로그만 남기고 패널은 정상 동작
        }

        // 패널 상태 업데이트
        set(
          state => {
            const nextPanels = new Map(state.panels);
            nextPanels.set(scopeKey, {
              panel: {
                type: 'list',
                panelResource,
                viewMode,
                panelId,
              },
            });
            return { panels: nextPanels };
          },
          undefined,
          `${STORE_NAME}/openBottomPanel`,
        );
      },

      // ========================================================================
      // openSidePanel (Detail 화면용 우측 패널)
      // ========================================================================

      openSidePanel: params => {
        const { scopeKey, panelResource, panelId, viewMode } = params;

        // Embedded 모드 차단
        if (viewMode === 'embedded') {
          console.warn(
            `[PanelNavigation] Blocked: embedded mode cannot create panel (${scopeKey})`,
          );
          return;
        }

        // Dedup: 동일한 패널 재호출 방지
        const existing = get().panels.get(scopeKey);
        if (isSamePanel(existing, params)) {
          return;
        }

        // Bridge Mode 체크
        const config = usePanelScopeRegistry.getState().getScopeConfig(scopeKey);
        if (config?.bridgeMode === 'off') {
          console.info(`[PanelNavigation] bridge-off mode: ${scopeKey}`);
          // @phase7-extension: 레거시 연동 차단 지점
          // Phase 2에서는 로그만 남기고 패널은 정상 동작
        }

        // Phase 4: SelectionChannel에서 selection 스냅샷 조회
        const selection = useSelectionChannel.getState().getSelection(scopeKey);

        // 패널 상태 업데이트
        set(
          state => {
            const nextPanels = new Map(state.panels);
            nextPanels.set(scopeKey, {
              panel: {
                type: 'detail',
                panelResource,
                panelId,
                viewMode,
              },
              // Phase 4: selection 스냅샷 저장
              // - SelectionChannel이 원본 관리
              // - PanelNavigation은 가드/리셋 편의를 위해 스냅샷 보관
              selection: selection ?? undefined,
            });
            return { panels: nextPanels };
          },
          undefined,
          `${STORE_NAME}/openSidePanel`,
        );
      },

      // ========================================================================
      // Legacy Aliases (Backward Compatibility)
      // ========================================================================

      /**
       * @deprecated Use openBottomPanel instead
       */
      openListPanel: params => {
        get().openBottomPanel(params);
      },

      /**
       * @deprecated Use openSidePanel instead
       */
      openDetailPanel: params => {
        get().openSidePanel(params);
      },

      // ========================================================================
      // closePanel
      // ========================================================================

      closePanel: scopeKey => {
        set(
          state => {
            const nextPanels = new Map(state.panels);
            nextPanels.delete(scopeKey);
            return { panels: nextPanels };
          },
          undefined,
          `${STORE_NAME}/closePanel`,
        );
      },

      // ========================================================================
      // resetScope
      // ========================================================================

      resetScope: scopeKey => {
        set(
          state => {
            const nextPanels = new Map(state.panels);
            nextPanels.delete(scopeKey);
            return { panels: nextPanels };
          },
          undefined,
          `${STORE_NAME}/resetScope`,
        );
      },

      // ========================================================================
      // syncWithURL
      // ========================================================================

      syncWithURL: () => {
        // @phase7-extension: URL serialization 연동 지점
        // Phase 2에서는 no-op
        // 향후 URL과 패널 상태를 동기화하는 로직 구현 예정
      },
    }),
    { name: STORE_NAME },
  ),
);
